/**
 * Workbook Performance Panel Component
 * Affiche l'analyse des performances et les opportunités d'optimisation
 */

import React, { useState } from 'react';
import { LoadingSpinner } from '../common/LoadingSpinner';
import type { WorkbookPerformanceReport, PerformanceIssue } from '@excel-ai-studio/shared';
import { callAnalyzeAPI } from '../../services/workbook-brain-api.service';
import { createPerformanceSheet, createQuickWinsSheet } from '../../services/workbook-brain-writer.service';

interface WorkbookPerformancePanelProps {
  onAnalysisComplete?: (report: WorkbookPerformanceReport) => void;
}

export const WorkbookPerformancePanel: React.FC<WorkbookPerformancePanelProps> = ({
  onAnalysisComplete,
}) => {
  const [report, setReport] = useState<WorkbookPerformanceReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterImpact, setFilterImpact] = useState<'all' | 'high' | 'medium' | 'low'>('all');
  const [expandedIssue, setExpandedIssue] = useState<number | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const analyzePerformance = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await callAnalyzeAPI({
        includeScanning: true,
        includePerformanceAnalysis: true,
      });

      if (!result.performanceReport) {
        throw new Error('Aucun rapport de performance retourné par le serveur');
      }

      setReport(result.performanceReport);
      onAnalysisComplete?.(result.performanceReport);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible d\'analyser les performances');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreatePerformanceSheet = async () => {
    if (!report) return;
    
    try {
      setMessage(null);
      await createPerformanceSheet(report.issues.map(i => i.description));
      setMessage({ type: 'success', text: '✅ Feuille d\'améliorations créée avec succès' });
      setTimeout(() => setMessage(null), 5000);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Erreur lors de la création de la feuille';
      setMessage({ type: 'error', text: `❌ ${errorMsg}` });
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const handleCreateQuickWinsSheet = async () => {
    if (!report) return;
    
    try {
      setMessage(null);
      await createQuickWinsSheet(report.quickWins);
      setMessage({ type: 'success', text: '✅ Feuille des gains rapides créée avec succès' });
      setTimeout(() => setMessage(null), 5000);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Erreur lors de la création de la feuille';
      setMessage({ type: 'error', text: `❌ ${errorMsg}` });
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const handleExportToClipboard = async () => {
    if (!report) return;
    const lines = [
      `Rapport de performance — Score : ${report.score}/100 (${report.overallRating})`,
      report.estimatedRecalcTimeMs ? `Temps de recalcul estimé : ${report.estimatedRecalcTimeMs}ms` : '',
      '',
      'Gains rapides :',
      ...report.quickWins.map((w) => `  - ${w}`),
      '',
      'Goulots :',
      ...report.bottlenecks.map((b) => `  - ${b}`),
      '',
      'Problèmes :',
      ...report.issues.map(
        (i) =>
          `  [${i.impact.toUpperCase()}] ${i.type}${i.sheetName ? ` (${i.sheetName})` : ''} — ${i.description} | ${i.recommendation}${i.estimatedImprovementPercent ? ` (+${i.estimatedImprovementPercent}%)` : ''}`
      ),
    ].filter(Boolean);
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setMessage({ type: 'success', text: 'Rapport copié dans le presse-papiers' });
      setTimeout(() => setMessage(null), 3000);
    } catch {
      setMessage({ type: 'error', text: 'Impossible de copier dans le presse-papiers' });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const getImpactColor = (impact: string): string => {
    switch (impact) {
      case 'high': return '#d32f2f';
      case 'medium': return '#f57c00';
      case 'low': return '#388e3c';
      default: return '#1976d2';
    }
  };

  const getImpactIcon = (impact: string): string => {
    switch (impact) {
      case 'high': return '🔴';
      case 'medium': return '🟡';
      case 'low': return '🟢';
      default: return '⚪';
    }
  };

  const getImpactLabel = (impact: string): string => {
    switch (impact) {
      case 'all': return 'Tous';
      case 'high': return 'Élevé';
      case 'medium': return 'Moyen';
      case 'low': return 'Faible';
      default: return impact;
    }
  };

  const getRatingColor = (rating: string): string => {
    switch (rating) {
      case 'excellent': return '#2e7d32';
      case 'good': return '#388e3c';
      case 'fair': return '#f57c00';
      case 'poor': return '#d32f2f';
      default: return '#1976d2';
    }
  };

  const getRatingLabel = (rating: string): string => {
    switch (rating) {
      case 'excellent': return 'EXCELLENT';
      case 'good': return 'BON';
      case 'fair': return 'PASSABLE';
      case 'poor': return 'FAIBLE';
      default: return rating.toUpperCase();
    }
  };

  const getRatingEmoji = (rating: string): string => {
    switch (rating) {
      case 'excellent': return '⭐⭐⭐⭐⭐';
      case 'good': return '⭐⭐⭐⭐';
      case 'fair': return '⭐⭐⭐';
      case 'poor': return '⭐⭐';
      default: return '⭐';
    }
  };

  const getFilteredIssues = (): PerformanceIssue[] => {
    if (!report) return [];
    if (filterImpact === 'all') return report.issues;
    return report.issues.filter((i) => i.impact === filterImpact);
  };

  if (isLoading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <LoadingSpinner label="Analyse des performances en cours..." />
      </div>
    );
  }

  const filteredIssues = getFilteredIssues();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* En-tête */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>⚡ Analyse des performances</h3>
        <button
          onClick={analyzePerformance}
          style={{ padding: '8px 16px', backgroundColor: '#2196F3', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
        >
          Analyser les performances
        </button>
      </div>

      {error && (
        <div style={{ padding: '12px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px', fontSize: '13px' }}>
          ❌ {error}
        </div>
      )}

      {message && (
        <div style={{ padding: '12px', backgroundColor: message.type === 'success' ? '#e8f5e9' : '#ffebee', color: message.type === 'success' ? '#2e7d32' : '#c62828', borderRadius: '4px', fontSize: '13px' }}>
          {message.text}
        </div>
      )}

      {/* Carte du score de performance */}
      {report && (
        <div style={{ padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '4px', border: `3px solid ${getRatingColor(report.overallRating)}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>Score de performance</div>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#333' }}>
                {report.score}/100
              </div>
              <div style={{ fontSize: '12px', marginTop: '4px' }}>
                {getRatingEmoji(report.overallRating)}
              </div>
            </div>
            <div style={{ textAlign: 'right', fontSize: '12px', color: '#666' }}>
              <div style={{ marginBottom: '8px' }}>
                <div style={{ fontWeight: 'bold', color: getRatingColor(report.overallRating) }}>
                  {getRatingLabel(report.overallRating)}
                </div>
              </div>
              {report.estimatedRecalcTimeMs && (
                <div>
                  <div style={{ fontSize: '11px', color: '#999' }}>Temps de recalcul estimé</div>
                  <div style={{ fontWeight: 'bold' }}>{report.estimatedRecalcTimeMs}ms</div>
                </div>
              )}
            </div>
          </div>
          <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
            <button
              onClick={handleCreatePerformanceSheet}
              style={{
                flex: 1,
                padding: '8px 12px',
                backgroundColor: '#ff9800',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 'bold',
              }}
            >
              📊 Améliorations
            </button>
            <button
              onClick={handleCreateQuickWinsSheet}
              style={{
                flex: 1,
                padding: '8px 12px',
                backgroundColor: '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 'bold',
              }}
            >
              💡 Gains rapides
            </button>
            <button
              onClick={handleExportToClipboard}
              style={{
                padding: '8px 12px',
                backgroundColor: '#616161',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 'bold',
              }}
            >
              Copier
            </button>
          </div>
        </div>
      )}

      {/* Gains rapides */}
      {report && report.quickWins.length > 0 && (
        <div style={{ padding: '12px', backgroundColor: '#e8f5e9', borderRadius: '4px', border: '1px solid #4caf50' }}>
          <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#2e7d32', marginBottom: '8px' }}>
            💡 Gains rapides
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {report.quickWins.map((win, idx) => (
              <div key={idx} style={{ fontSize: '12px', color: '#1b5e20' }}>
                ✓ {win}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Goulots d'étranglement */}
      {report && report.bottlenecks.length > 0 && (
        <div style={{ padding: '12px', backgroundColor: '#fff3e0', borderRadius: '4px', border: '1px solid #ff9800' }}>
          <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#e65100', marginBottom: '8px' }}>
            🚧 Goulots d'étranglement
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {report.bottlenecks.map((bottleneck, idx) => (
              <div key={idx} style={{ fontSize: '12px', color: '#bf360c' }}>
                • {bottleneck}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Boutons de filtre */}
      {report && (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {(['all', 'high', 'medium', 'low'] as const).map((impact) => (
            <button
              key={impact}
              onClick={() => setFilterImpact(impact)}
              style={{
                padding: '6px 12px',
                backgroundColor: filterImpact === impact ? getImpactColor(impact) : '#f0f0f0',
                color: filterImpact === impact ? 'white' : '#666',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: filterImpact === impact ? 'bold' : 'normal',
              }}
            >
              {getImpactLabel(impact)}
            </button>
          ))}
        </div>
      )}

      {/* Liste des problèmes */}
      {report && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {filteredIssues.length > 0 ? (
            filteredIssues.map((issue, idx) => (
              <div
                key={idx}
                onClick={() => setExpandedIssue(expandedIssue === idx ? null : idx)}
                style={{
                  padding: '12px',
                  backgroundColor: 'white',
                  borderLeft: `4px solid ${getImpactColor(issue.impact)}`,
                  borderRadius: '4px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  border: expandedIssue === idx ? `2px solid ${getImpactColor(issue.impact)}` : 'none',
                  borderLeftWidth: '4px',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '16px' }}>{getImpactIcon(issue.impact)}</span>
                      <span style={{ fontWeight: 'bold', fontSize: '13px', color: '#333' }}>{issue.type}</span>
                      {issue.sheetName && (
                        <span style={{ fontSize: '11px', backgroundColor: '#e3f2fd', color: '#1976d2', padding: '2px 6px', borderRadius: '3px' }}>
                          {issue.sheetName}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                      {issue.description}
                    </div>
                  </div>
                  <span style={{ fontSize: '18px', marginLeft: '8px' }}>
                    {expandedIssue === idx ? '▼' : '▶'}
                  </span>
                </div>

                {expandedIssue === idx && (
                  <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #eee', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#999', marginBottom: '4px' }}>
                        RECOMMANDATION
                      </div>
                      <div style={{ fontSize: '12px', color: '#333' }}>{issue.recommendation}</div>
                    </div>
                    {issue.estimatedImprovementPercent && (
                      <div>
                        <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#999', marginBottom: '4px' }}>
                          AMÉLIORATION ESTIMÉE
                        </div>
                        <div style={{ fontSize: '12px', color: '#2e7d32', fontWeight: 'bold' }}>
                          +{issue.estimatedImprovementPercent}% de gain de performance
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div style={{ padding: '16px', textAlign: 'center', color: '#999', fontSize: '13px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
              ✅ Aucun problème de performance trouvé dans cette catégorie
            </div>
          )}
        </div>
      )}
    </div>
  );
};
